import { watch, nextTick } from "vue";

const setupWatchForMenuEventListener = (source) => watch(source, async (value, oldValue, onCleanup) => {
  // T15-1 「実施状況」画面のメニュー（ミートボールメニュー・未提出者リスト）関連のイベントリスナーの設定処理
  //
  // モックの menu.js は、画面がロードされたときのみイベントリスナーを設定し直す前提のロジックだったため、
  // vue 上で再レンダリングされたときも機能するように、おもに以下の部分は書き換えている
  //
  // - 処理全体はHTMLの読み込み完了後（DOMContentLoaded）ではなく、特定のソースが変更されたときに発火されるように watch で監視
  // - 各イベントリスナーは、設定・取り外しができるように、無名関数として定義されていたところは名前付き関数に変更
  // - watch が再実行される直前に、前回設定されたイベントリスナーが取り外されるように、クリーンアップ関数を登録

  // DOM更新の完了を待つ
  await nextTick();

  const buttons = document.querySelectorAll(".menu-button");
  const menus = document.querySelectorAll(".c-table-context-menu");
  const tableContainer = document.querySelector(".c-table-test-list-wrapper");

  // 現在アクティブなメニューとボタンを追跡
  let activeMenu = null;
  let activeButton = null;

  // メニュー位置を計算
  function updateMenuPosition() {
    if (!activeMenu || !activeButton) return;

    // メニューを一時的に表示して、サイズを正確に取得
    activeMenu.style.display = "block";
    activeMenu.style.visibility = "hidden"; // 計測中は非表示にする

    // ボタンの位置
    const buttonRect = activeButton.getBoundingClientRect();

    // メニューのサイズ
    const menuHeight = activeMenu.offsetHeight;
    const menuWidth = activeMenu.offsetWidth;

    // 画面端からの距離
    const distanceFromBottom = window.innerHeight - buttonRect.bottom;
    const distanceFromRight = window.innerWidth - buttonRect.right;

    // メニュー位置の決定
    let menuLeft, menuTop;

    // 縦位置
    if (distanceFromBottom < menuHeight) {
      menuTop = buttonRect.top - menuHeight; // ボタンの上
    } else {
      menuTop = buttonRect.bottom; // ボタンの下
    }

    // 横位置
    if (distanceFromRight < menuWidth) {
      menuLeft = buttonRect.left - (menuWidth - buttonRect.width); // 左寄せ
    } else {
      menuLeft = buttonRect.left; // 右寄せ
    }

    // 画面外にはみ出す場合の調整
    if (menuLeft < 0) menuLeft = 0;
    if (menuTop < 0) menuTop = 0;

    // メニュー位置を最終設定（ビューポート基準の固定位置）
    activeMenu.style.position = 'fixed';
    activeMenu.style.left = `${menuLeft}px`;
    activeMenu.style.top = `${menuTop}px`;
    activeMenu.style.visibility = "visible"; // 表示を戻す
  }

  // ボタンクリックイベント
  function clickButton(event) {
    const button = event.currentTarget;

    // メニューを取得
    const menuId = button.getAttribute("data-menu");
    const menu = document.getElementById(menuId);

    if (!menu) return; // メニューが存在しない場合は処理をスキップ

    // 同じメニューが開いている場合は閉じる
    if (button.classList.contains("open")) {
      menu.style.display = "none";
      buttons.forEach(btn => btn.classList.remove("open"));
      activeMenu = null;
      activeButton = null;
      return;
    }

    // すべてのメニューを閉じる
    menus.forEach(m => m.style.display = "none");
    buttons.forEach(btn => btn.classList.remove("open"));

    // 現在のメニューとボタンを記録
    activeMenu = menu;
    activeButton = button;

    // メニュー位置を更新（表示も内部で行われる）
    updateMenuPosition();

    // ボタンをアクティブ化
    button.classList.add("open");

    // イベント伝播を停止
    event.stopPropagation();
  }
  buttons.forEach(button => button.addEventListener("click", clickButton));

  // メニュー内クリックの伝播を停止
  function clickMenu(event) {
    event.stopPropagation();
  }
  menus.forEach(menu => menu.addEventListener("click", clickMenu));

  // ドキュメントクリックでメニューを閉じる
  function clickDocument() {
    menus.forEach(menu => menu.style.display = "none");
    buttons.forEach(btn => btn.classList.remove("open"));
    activeMenu = null;
    activeButton = null;
  }
  document.addEventListener("click", clickDocument);

  // メニューを閉じる関数
  function closeMenu() {
    if (activeMenu) {
      activeMenu.style.display = "none";
      if (activeButton) activeButton.classList.remove("open");
      activeMenu = null;
      activeButton = null;
    }
  }

  // スクロールイベントごとにメニューを閉じる
  if (tableContainer) { // テーブルコンテナが存在する場合のみ
    tableContainer.addEventListener("scroll", closeMenu);
  }

  window.addEventListener("scroll", closeMenu);

  // ウィンドウリサイズ時のメニュー位置更新
  function resizeWindow() {
    if (activeMenu && activeButton) {
      updateMenuPosition();
    }
  }
  window.addEventListener("resize", resizeWindow);

  // watch が再実行される直前に実行されるクリーンアップ関数を登録
  // 再実行されたときにイベントリスナーが重複設定されないように、前回設定されたイベントリスナーは取り外しておく
  // vue 3.5+ では、onWatcherCleanup に置き換えられるはずなので、vue のバージョンアップ時は置き換えてください
  // https://ja.vuejs.org/api/reactivity-core.html#watch
  onCleanup(() => {
    buttons.forEach(button => button.removeEventListener("click", clickButton));
    menus.forEach(menu => menu.removeEventListener("click", clickMenu));
    document.removeEventListener("click", clickDocument);
    if (tableContainer) {
      tableContainer.removeEventListener("scroll", closeMenu);
    }
    window.removeEventListener("scroll", closeMenu);
    window.removeEventListener("resize", resizeWindow);
  });
});

export { setupWatchForMenuEventListener }
